"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";

import { ReactNode } from "react";
import Link from "next/link";
import {
  ArrowRight,
  CalendarCheck,
  ShoppingBag,
  Handshake,
  Ticket,
  GraduationCap,
  PhoneCall,
  Plug,
} from "lucide-react";
import { cn } from "@/lib/utils";
import Mascot from "@/components/platform/v2/Mascot";
import { PageCta } from "@/components/platform/v2/PageEdge";

interface Scenario {
  id: string;
  icon: typeof CalendarCheck;
  kicker: string;
  title: string;
  fit: string;
  flow: string[];
  features: string[];
  thesis: string;
  note?: string;
  link: string;
  span: string;
  mockup: "booking" | "cart" | "deal" | "ticket" | "course" | "manual";
}

const SCENARIOS: Scenario[] = [
  {
    id: "services",
    icon: CalendarCheck,
    kicker: "Услуги и сервис",
    title: "Когда клиент записывается, приходит и возвращается",
    fit: "Клиники, салоны, сервисные компании, консультационный бизнес, специалисты — все, кто работает по записи.",
    flow: [
      "Запись",
      "CRM",
      "Напоминание",
      "Услуга",
      "Оплата",
      "Повторный визит",
    ],
    features: [
      "Запись",
      "CRM",
      "Личный кабинет",
      "Напоминания",
      "Чат",
      "Лояльность",
    ],
    thesis: "Не просто принять заявку. Сохранить клиента и вернуть его снова.",
    link: "/platform/solutions/services",
    span: "lg:col-span-3",
    mockup: "booking",
  },
  {
    id: "commerce",
    icon: ShoppingBag,
    kicker: "Торговля и e-commerce",
    title: "Когда нужно не просто продать товар, а сохранить покупателя",
    fit: "Магазины, производители, бренды, локальная розница, компании с каталогом.",
    flow: ["Каталог", "Заказ", "Оплата", "Бонусы", "Повторная покупка"],
    features: [
      "Каталог",
      "Корзина",
      "Оплаты",
      "Заказы",
      "CRM",
      "Лояльность",
      "Рефералы",
    ],
    thesis: "Покупка — начало отношений, а не конец сделки.",
    note: "Интеграции с маркетплейсами — направление развития платформы.",
    link: "/platform/solutions/commerce",
    span: "lg:col-span-3",
    mockup: "cart",
  },
  {
    id: "b2b",
    icon: Handshake,
    kicker: "B2B и продажи",
    title: "Когда продажа начинается с лида, а заканчивается не сразу",
    fit: "B2B-компании, производители, поставщики, дистрибьюторы, агентства, команды с менеджерами.",
    flow: ["Лид", "Менеджер", "Сделка", "Документы", "Оплата"],
    features: [
      "CRM",
      "Сделки",
      "Задачи",
      "Роли сотрудников",
      "Чаты",
      "Документы",
      "Автоматизация",
    ],
    thesis:
      "Менеджер видит не только контакт клиента, а весь рабочий контекст.",
    link: "/platform/solutions/b2b",
    span: "lg:col-span-3",
    mockup: "deal",
  },
  {
    id: "events",
    icon: Ticket,
    kicker: "Мероприятия и сообщества",
    title: "Когда людей нужно не просто собрать, а объединить",
    fit: "Форумы, конференции, выставки, клубы, ассоциации, бизнес-сообщества, организаторы мероприятий.",
    flow: [
      "Регистрация",
      "Билет и QR",
      "Программа",
      "Нетворкинг",
      "После события",
    ],
    features: [
      "Приложение мероприятия",
      "Регистрация",
      "QR и билеты",
      "Программа",
      "Нетворкинг",
      "Чаты",
      "Партнёры",
    ],
    thesis: "Мероприятие живёт не два дня, а всё время вокруг сообщества.",
    link: "/platform/solutions/events",
    span: "lg:col-span-3",
    mockup: "ticket",
  },
  {
    id: "education",
    icon: GraduationCap,
    kicker: "Образование, эксперты и подписки",
    title: "Когда клиент проходит путь, а не просто покупает доступ",
    fit: "Школы, образовательные проекты, эксперты, клубы, подписочные продукты, наставники.",
    flow: ["Регистрация", "Контент", "Квиз", "Подписка", "Повторная продажа"],
    features: [
      "Личный кабинет",
      "Квизы",
      "Сценарии",
      "AI",
      "Подписки",
      "Чаты",
      "CRM",
    ],
    thesis: "Путь клиента виден целиком — от первого шага до продления.",
    link: "/platform/solutions/education",
    span: "lg:col-span-3",
    mockup: "course",
  },
  {
    id: "offline",
    icon: PhoneCall,
    kicker: "Онлайн + офлайн клиенты",
    title: "Когда часть клиентов вообще не живёт в цифровом контуре",
    fit: "Офлайн-бизнес, компании с офисами и торговыми точками, продажи по телефону, B2B, готовая клиентская база.",
    flow: [
      "Звонок или визит",
      "Менеджер добавил в CRM",
      "Сделка и задача",
      "История",
      "Цифровой профиль позже",
    ],
    features: [
      "Ручное создание клиента",
      "Офлайн-сделки",
      "Задачи",
      "История",
      "Связь с профилем",
    ],
    thesis:
      "Чтобы работать в VCENTRE.ONE, клиенту не обязательно устанавливать приложение или регистрироваться.",
    link: "/platform/solutions/online-offline",
    span: "lg:col-span-3",
    mockup: "manual",
  },
];

const Bar = ({ w, accent }: { w: string; accent?: boolean }) => (
  <span
    className={cn(
      "block h-2 rounded-full",
      accent ? "bg-primary/80" : "bg-foreground/15",
    )}
    style={{ width: w }}
  />
);

const MockupFrame = ({ children }: { children: ReactNode }) => (
  <div className="rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-4">
    <div className="mb-3 flex items-center gap-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-primary/70" />
      <span className="h-1.5 w-1.5 rounded-full bg-foreground/20" />
      <span className="h-1.5 w-1.5 rounded-full bg-foreground/20" />
    </div>
    {children}
  </div>
);

const Mockup = ({ kind }: { kind: Scenario["mockup"] }) => {
  if (kind === "booking") {
    return (
      <MockupFrame>
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 21 }).map((_, i) => (
            <span
              key={i}
              className={cn(
                "aspect-square rounded-[5px] transition-colors duration-500",
                [4, 9, 15].includes(i) ? "bg-primary/80" : "bg-foreground/10",
              )}
            />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] px-3 py-2">
          <span className="text-[11px] font-semibold text-foreground">
            14:30 · Приём
          </span>
          <span className="text-[11px] font-semibold text-primary">
            Подтверждено
          </span>
        </div>
      </MockupFrame>
    );
  }
  if (kind === "cart") {
    return (
      <MockupFrame>
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className="aspect-square rounded-lg bg-foreground/10"
            />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Bar w="52%" />
          <span className="text-[11px] font-bold text-primary">Оплатить</span>
        </div>
      </MockupFrame>
    );
  }
  if (kind === "deal") {
    return (
      <MockupFrame>
        <div className="grid grid-cols-3 gap-1.5">
          {["Лид", "Сделка", "Оплата"].map((s, i) => (
            <div
              key={s}
              className="rounded-lg border border-border/50 bg-[hsl(var(--charcoal))] p-2"
            >
              <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                {s}
              </span>
              <span
                className={cn(
                  "mt-2 block h-1.5 rounded-full",
                  i === 1 ? "bg-primary/80" : "bg-foreground/15",
                )}
              />
              <span className="mt-1.5 block h-1.5 w-2/3 rounded-full bg-foreground/10" />
            </div>
          ))}
        </div>
      </MockupFrame>
    );
  }
  if (kind === "ticket") {
    return (
      <MockupFrame>
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 shrink-0 grid-cols-4 gap-[3px] rounded-lg bg-foreground/5 p-1.5">
            {Array.from({ length: 16 }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  "rounded-[2px]",
                  i % 3 === 0 ? "bg-primary/80" : "bg-foreground/20",
                )}
              />
            ))}
          </div>
          <div className="flex-1 space-y-2">
            <Bar w="70%" accent />
            <Bar w="45%" />
            <Bar w="58%" />
          </div>
        </div>
      </MockupFrame>
    );
  }
  if (kind === "course") {
    return (
      <MockupFrame>
        <div className="space-y-2">
          {[100, 64, 28].map((p, i) => (
            <div key={p} className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-muted-foreground">
                0{i + 1}
              </span>
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-foreground/10">
                <span
                  className="block h-full rounded-full bg-primary/80"
                  style={{ width: `${p}%` }}
                />
              </span>
            </div>
          ))}
        </div>
      </MockupFrame>
    );
  }
  return (
    <MockupFrame>
      <div className="space-y-2">
        <div className="flex items-center justify-between rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] px-3 py-2">
          <span className="text-[11px] font-semibold text-foreground">
            Новый клиент вручную
          </span>
          <span className="text-[11px] font-bold text-primary">+</span>
        </div>
        <Bar w="80%" />
        <Bar w="55%" />
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          без приложения
        </span>
      </div>
    </MockupFrame>
  );
};

const Flow = ({ steps }: { steps: string[] }) => (
  <div className="relative pt-3">
    <span className="absolute left-0 top-0 h-px w-full bg-foreground/10" />
    <span className="absolute left-0 top-0 h-px w-0 bg-primary transition-all duration-700 ease-out group-hover:w-full" />
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {steps.map((step, i) => (
        <span
          key={step}
          className="flex items-center gap-2 text-[11px] font-semibold text-muted-foreground"
        >
          {i > 0 && <ArrowRight className="h-3 w-3 text-primary/60" />}
          {step}
        </span>
      ))}
    </div>
  </div>
);

const ScenarioCard = ({ item }: { item: Scenario }) => {
  const Icon = item.icon;
  return (
    <article
      className={cn(
        "group surface flex flex-col gap-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40",
        item.span,
        item.id === "offline" && "border-primary/30",
      )}
    >
      <div className="flex flex-col gap-5">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2">
            <span className="eyebrow">{item.kicker}</span>
          </div>
          <IconHeading
            icon={<Icon className="h-5 w-5 text-primary" />}
            className="text-lg font-bold leading-snug tracking-[-0.01em] text-foreground sm:text-xl"
          >
            {item.title}
          </IconHeading>
          <p className="body-text">{item.fit}</p>
          <div className="flex flex-wrap gap-2">
            {item.features.map((f) => (
              <span key={f} className="pill pill-neutral">
                {f}
              </span>
            ))}
          </div>
        </div>
        <div className="w-full shrink-0">
          <Mockup kind={item.mockup} />
        </div>
      </div>

      <Flow steps={item.flow} />

      <p className="text-sm font-semibold text-foreground">{item.thesis}</p>
      {item.note && (
        <p className="text-xs text-muted-foreground">{item.note}</p>
      )}

      <Link href={item.link} className="btn-link mt-auto">
        Посмотреть решение <ArrowRight className="h-4 w-4" />
      </Link>
    </article>
  );
};

const AudienceFit = () => {
  return (
    <section id="fit" className="section-y">
      <div className="container-page">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
          <div className="max-w-3xl space-y-4">
            <p className="eyebrow">Кому подходит</p>
            <h2 className="display-2">Кому подходит VCENTRE.ONE</h2>
            <p className="lede">
              Если у вас есть клиенты, продажи, сотрудники или процессы, которые
              сегодня разбросаны между разными сервисами — скорее всего, часть
              этой работы можно собрать в VCENTRE.ONE.
            </p>
            <div className="flex flex-col gap-3 pt-2 sm:flex-row">
              <Link href="/signin?source=solutions" className="btn-primary">
                Подобрать решение для моего бизнеса
              </Link>
              <a href="#scenarios" className="btn-ghost">
                Посмотреть все направления
              </a>
            </div>
            <p className="handwritten -rotate-2">«Скорее всего, и вам.»</p>
          </div>
          <Mascot
            pose="point"
            note="«Скорее всего, и вам.»"
            imageClassName="max-w-[200px]"
            noteClassName="-top-2 right-0"
            className="hidden lg:flex"
          />
        </div>

        <div
          id="scenarios"
          className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-6"
        >
          {SCENARIOS.map((item) => (
            <ScenarioCard key={item.id} item={item} />
          ))}
        </div>

        {/* Уже есть CRM / 1С */}
        <div className="surface mt-5 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Plug className="h-5 w-5 text-primary" />
                <span className="eyebrow">Уже есть инфраструктура</span>
              </div>
              <h3 className="display-3">
                У вас уже есть CRM, 1С или другие сервисы?
              </h3>
              <p className="body-text">
                VCENTRE.ONE не обязательно заменяет существующую инфраструктуру.
                Платформа может стать клиентским и операционным слоем поверх уже
                работающих систем.
              </p>
              <p className="text-base font-bold text-foreground">
                Не ломаем работающие процессы. Соединяем их.
              </p>
              <Link
                href="/signin?source=solutions&focus=existing-stack"
                className="btn-primary"
              >
                Показать, что у нас уже есть
              </Link>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                "Подключить приложение",
                "Создать связанный сайт",
                "Передавать данные в CRM",
                "Работать с 1С",
                "Принимать оплаты",
                "Подключать внешние каналы",
              ].map((i) => (
                <li
                  key={i}
                  className="surface-quiet text-sm font-semibold text-foreground"
                >
                  <span className="mr-2 text-primary">—</span>
                  {i}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Финальный CTA */}
        <PageCta
          className="mt-5"
          title="Не нашли точное описание своего бизнеса?"
          text={
            <p>
              VCENTRE.ONE не строится вокруг одной отрасли. Мы собираем нужный
              клиентский и бизнес-процесс под конкретную модель компании.
            </p>
          }
          actions={
            <>
              <Link
                href="/signin?source=solutions-bottom"
                className="btn-primary"
              >
                Собрать своё решение
              </Link>
              <Link href="/platform/pricing" className="btn-ghost">
                Посмотреть тарифы
              </Link>
            </>
          }
          visual={
            <img
              src={"/assets/images/platform/solutions-mascot-cta.png"}
              alt="Утёнок VCENTRE с ракетой и ноутбуком — Запускаем быстро"
              className="mx-auto w-full max-w-[280px] object-contain sm:max-w-[340px]"
            />
          }
        />
      </div>
    </section>
  );
};

export default AudienceFit;
